import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogTitle, DialogBody } from '../dialog';
import SearchableListbox from '../SearchableListbox';
import { useUser } from '../UserContext';
import { useClients, useContracts, useOrdenesTrabajoRaw } from '../../contexts/DataContext';
import { colorDe, inicialesDe } from '../AsignadoBubble';
import { useOrgMiembros } from '../../hooks/useOrgMiembros';
import { useOtAcciones, useOtConfig } from '../../hooks/useOrdenesTrabajo';
import { getEmisorNombre } from '../../utils/calculations';
import { mapAfianzadoraName } from '../../utils/afianzadoras';
import {
  OT_TIPOS,
  DESCRIPCION_MAX_LEN,
  validarOt,
  tituloDerivado,
  folioTexto,
  fasesDe,
  requiereFianza,
  ofreceFianza,
  cargaPorPersona,
  isAbierta,
} from '../../utils/ordenesTrabajo';

const field =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

const dateInputToMs = (v) => {
  if (!v) return null;
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d, 12).getTime();
};
// contracts.createdAt is a Firestore Timestamp on most docs and ms on some.
const msDe = (v) => (v?.toMillis ? v.toMillis() : (v?.seconds ? v.seconds * 1000 : Number(v) || 0));
const fechaCorta = (v) => {
  const ms = msDe(v);
  return ms ? new Date(ms).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

const msToDateInput = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Who to assign, with what they already carry. Fran (2026-10-17): "cuando creo
 * una orden debo poder ver la carga actual de las personas en el equipo de X
 * para ver a quién asignarla; y si este cliente está asignado a algún
 * ejecutivo en particular, lo debemos mostrar también".
 *
 * Members are grouped by the equipos chosen for the OT (everyone when none
 * is chosen); each row shows open OTs as a bar and a tag when the person is
 * an ejecutivo of the fiado or the contrato (assignedUsers). Ejecutivos sort
 * first, then by lightest load.
 */
export function PanelAsignacion({ miembros, equipos, equiposElegidos, carga, ejecutivos, asignados, meUid, onToggle }) {
  // Fran (2026-10-19): the coordinador sometimes needs someone outside the
  // fase's equipo. Hidden members come in under "Ver a todos".
  const [todos, setTodos] = useState(false);
  const porId = {};
  miembros.forEach((m) => { porId[m.id] = m; });
  const maxCarga = Math.max(1, ...Object.values(carga));
  const orden = (a, b) => {
    const ea = ejecutivos.has(a) ? 0 : 1;
    const eb = ejecutivos.has(b) ? 0 : 1;
    if (ea !== eb) return ea - eb;
    return (carga[a] || 0) - (carga[b] || 0);
  };
  // A person in two chosen equipos is listed once, under the first.
  const vistos = new Set();
  const tomar = (uids) => {
    const out = [];
    uids.forEach((u) => {
      if (!porId[u] || vistos.has(u)) return;
      vistos.add(u);
      out.push(u);
    });
    return out.sort(orden);
  };
  const grupos = equiposElegidos.length
    ? equiposElegidos.map((id) => {
      const eq = equipos.find((e) => e.id === id);
      return { id, nombre: eq?.nombre || id, uids: tomar(eq?.miembros || []) };
    })
    : [{ id: 'todos', nombre: null, uids: tomar(miembros.map((m) => m.id)) }];
  // Ticked people and the fiado's ejecutivos outside the shown equipos still
  // need a row; everyone else waits behind "Ver a todos".
  const sueltos = tomar(miembros.map((m) => m.id).filter((u) => asignados.includes(u) || ejecutivos.has(u)));
  if (sueltos.length) grupos.push({ id: 'otros', nombre: 'Fuera del equipo', uids: sueltos });
  const resto = miembros.map((m) => m.id).filter((u) => !vistos.has(u));
  if (todos && resto.length) grupos.push({ id: 'resto', nombre: 'Otros miembros', uids: tomar(resto) });

  const fila = (uid) => {
    const m = porId[uid];
    const on = asignados.includes(uid);
    const n = carga[uid] || 0;
    return (
      <li key={uid}>
        <button
          type="button"
          onClick={() => onToggle(uid)}
          className={`w-full flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors ${on ? 'bg-azul-surelia/10' : 'hover:bg-zinc-50'}`}
        >
          <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? 'bg-azul-surelia border-azul-surelia' : 'border-zinc-300 bg-white'}`}>
            {on && <span className="w-2 h-2 rounded-sm bg-white" />}
          </span>
          <span className={`flex items-center justify-center rounded-full w-6 h-6 text-[10px] font-semibold flex-shrink-0 ${colorDe(uid)}`}>
            {inicialesDe(m)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-sm text-zinc-800 truncate">{m.nombre || m.correo}{uid === meUid ? ' (yo)' : ''}</span>
              {ejecutivos.has(uid) && (
                <span className="rounded-full bg-verde-lima/40 px-1.5 py-0.5 text-[10px] font-medium text-azul-surelia whitespace-nowrap">Ejecutivo del fiado</span>
              )}
            </span>
            <span className="mt-0.5 block h-1 rounded bg-zinc-100">
              <span className="block h-1 rounded bg-cian-digital" style={{ width: `${(n / maxCarga) * 100}%` }} />
            </span>
          </span>
          <span className="text-xs tabular-nums text-zinc-500 whitespace-nowrap">{n} {n === 1 ? 'orden' : 'órdenes'}</span>
        </button>
      </li>
    );
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-2 space-y-2 max-h-72 overflow-y-auto">
      {grupos.map((g) => (
        <div key={g.id}>
          {g.nombre && <div className="px-2 pt-1 pb-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-400">{g.nombre}</div>}
          {g.uids.length === 0 ? (
            <div className="px-2 py-1 text-xs text-zinc-400">Sin miembros en este equipo</div>
          ) : (
            <ul className="space-y-0.5">{g.uids.map(fila)}</ul>
          )}
        </div>
      ))}
      {miembros.length === 0 && <div className="px-2 py-1 text-xs text-zinc-400">Sin miembros</div>}
      {resto.length > 0 && (
        <button
          type="button"
          onClick={() => setTodos((v) => !v)}
          className="px-2 py-1 text-xs text-azul-surelia hover:underline"
        >
          {todos ? 'Solo el equipo' : `Ver a todos (${resto.length} más)`}
        </button>
      )}
    </div>
  );
}

/**
 * Create / edit an Orden de Trabajo.
 *
 * Create is one form: tipo, name,
 * relation (fiado → contrato → fianza, every step optional — the OT can be
 * born before the contrato exists), fase inicial, equipos, asignados, fecha
 * compromiso. Edit changes name, description, fecha and relation; fase,
 * equipos and asignados are edited in place from the OtView.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Object|null} [props.ot]  - the OT being edited (null = create)
 * @param {Object|null} [props.preset]  - {clienteId, contratoId, fianzaId, tipo}
 * @param {Function} [props.onSaved]  - receives the created/edited OT
 */
export function OtDialog({ isOpen, onClose, ot = null, preset = null, onSaved }) {
  const { user } = useUser();
  const { clientsList, clientsById } = useClients();
  const { contractsList } = useContracts();
  const { ordenesTrabajo } = useOrdenesTrabajoRaw();
  const { miembros } = useOrgMiembros();
  const { config } = useOtConfig();
  const { crear, editar } = useOtAcciones();

  const isEdit = Boolean(ot?.id);

  const [tipo, setTipo] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [contratoId, setContratoId] = useState('');
  const [fianzaId, setFianzaId] = useState('');
  const [faseId, setFaseId] = useState('');
  const [equipos, setEquipos] = useState([]);
  const [asignados, setAsignados] = useState([]);
  const [fechaCompromiso, setFechaCompromiso] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Filled once per opening (see PendienteDialog for why: callers build the
  // preset inline and DataContext re-renders them on every snapshot).
  const presetRef = useRef(preset);
  presetRef.current = preset;
  const otRef = useRef(ot);
  otRef.current = ot;
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    if (!isOpen) return;
    const src = otRef.current || presetRef.current || {};
    setTipo(src.tipo || configRef.current.flujos[0]?.id || OT_TIPOS.FIANZA);
    setDescripcion(src.descripcion || '');
    setClienteId(src.clienteId || '');
    setContratoId(src.contratoId || '');
    setFianzaId(src.fianzaId || '');
    setFaseId(src.faseId || fasesDe(configRef.current, src.tipo)[0]?.id || '');
    setEquipos(Array.isArray(src.equipos) ? src.equipos : []);
    setAsignados(Array.isArray(src.asignados) ? src.asignados : (user?.uid ? [user.uid] : []));
    setFechaCompromiso(msToDateInput(src.fechaCompromiso));
    setError(null);
    setSubmitting(false);
  }, [isOpen, ot?.id, user?.uid]);

  // Each tipo has its own flow. The first fase of the chosen flow is the
  // default; it is re-resolved when the tipo changes or when the config
  // arrives after the dialog opened.
  const fases = useMemo(() => fasesDe(config, tipo), [config, tipo]);
  useEffect(() => {
    if (!isOpen || isEdit || !tipo) return;
    if (!faseId || !fases.some((f) => f.id === faseId)) setFaseId(fases[0]?.id || '');
  }, [isOpen, isEdit, tipo, fases, faseId]);

  const clienteOptions = useMemo(() => clientsList
    .filter((c) => !c.archived)
    .map((c) => ({ value: c.id, label: c.nombreEmpresa || 'Sin nombre' }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es')), [clientsList]);

  // Contratos BY NUMBER (Fran, 2026-10-17: "ponme por número de contrato")
  // across the whole org, each with the day it was uploaded, so the agente
  // can pick the contrato first and the fiado follows. Choosing a fiado first
  // narrows the list.
  const contratoOptions = useMemo(() => {
    const porCliente = {};
    clientsList.forEach((c) => { porCliente[c.id] = c.nombreEmpresa || ''; });
    return contractsList
      .filter((c) => c.clientId && (!clienteId || c.clientId === clienteId))
      .map((c) => {
        const numero = c.numeroContrato?.value || '';
        const t = c.tituloContrato?.value || '';
        const fiado = porCliente[c.clientId] || '';
        const subido = fechaCorta(c.createdAt);
        return {
          value: c.id,
          // First line: the number the agente types; second: what it is and
          // whose; at the right: when it was uploaded.
          label: numero || 'Sin número',
          description: [t, clienteId ? '' : fiado].filter(Boolean).join(' · ') || 'Sin título',
          meta: subido ? `subido ${subido}` : '',
          resumen: `${numero || 'Sin número'}${t ? ` · ${t}` : ''}`,
          orden: msDe(c.createdAt),
        };
      })
      .sort((a, b) => b.orden - a.orden);
  }, [contractsList, clientsList, clienteId]);

  const elegirContrato = (id) => {
    setContratoId(id);
    setFianzaId('');
    const c = contractsList.find((x) => x.id === id);
    if (c?.clientId && c.clientId !== clienteId) setClienteId(c.clientId);
  };

  const fianzaOptions = useMemo(() => {
    if (!contratoId) return [];
    const contrato = contractsList.find((c) => c.id === contratoId);
    return (contrato?.fianzas || []).map((f) => {
      const emisor = getEmisorNombre(f);
      const afianzadora = emisor ? (mapAfianzadoraName(emisor) || emisor) : '';
      const monto = Number(f.montoAfianzado);
      return {
        value: f.id,
        label: `#${f.numeroFianza || 'sin número'}`,
        description: [f.tipoFianza, afianzadora].filter(Boolean).join(' · '),
        meta: monto > 0 ? monto.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }) : '',
        resumen: `#${f.numeroFianza || 'sin número'}${f.tipoFianza ? ` · ${f.tipoFianza}` : ''}`,
      };
    });
  }, [contractsList, contratoId]);

  const afianzadoraDeFianza = useMemo(() => {
    if (!contratoId || !fianzaId) return null;
    const contrato = contractsList.find((c) => c.id === contratoId);
    const f = (contrato?.fianzas || []).find((x) => x.id === fianzaId);
    const emisor = f ? getEmisorNombre(f) : '';
    return emisor ? (mapAfianzadoraName(emisor) || emisor) : null;
  }, [contractsList, contratoId, fianzaId]);

  // An OT de fianza exists to create the fianza, so there is none to pick at
  // creation; it can be linked later from Editar. An endoso always names the
  // fianza it modifies.
  const pideFianza = ofreceFianza(tipo) || isEdit;

  // Open OTs per member, for the assignment panel.
  const carga = useMemo(() => {
    const m = {};
    cargaPorPersona((ordenesTrabajo || []).filter(isAbierta), miembros).forEach((r) => { m[r.uid] = r.total; });
    return m;
  }, [ordenesTrabajo, miembros]);

  // The ejecutivos of the chosen fiado and contrato (assignedUsers).
  const ejecutivos = useMemo(() => {
    const set = new Set();
    (clientsById[clienteId]?.assignedUsers || []).forEach((u) => set.add(u));
    (contractsList.find((c) => c.id === contratoId)?.assignedUsers || []).forEach((u) => set.add(u));
    return set;
  }, [clientsById, clienteId, contractsList, contratoId]);

  const toggleAsignado = (uid) => {
    setAsignados((prev) => (prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]));
  };

  const resetAndClose = () => {
    setTipo(null);
    setError(null);
    onClose?.();
  };

  const toggleEquipo = (id) => {
    setEquipos((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  };

  const guardar = async () => {
    const msg = validarOt({ tipo, faseId, fianzaId: fianzaId || null }, fases, config);
    if (msg) { setError(msg); return; }
    const contratoSel = contractsList.find((c) => c.id === contratoId) || null;
    const fianzaSel = (contratoSel?.fianzas || []).find((f) => f.id === fianzaId) || null;
    const titulo = tituloDerivado({
      fiado: clientsById[clienteId]?.nombreEmpresa || '',
      contratoNumero: contratoSel?.numeroContrato?.value || '',
      contratoTitulo: contratoSel?.tituloContrato?.value || '',
      fianzaNumero: fianzaSel?.numeroFianza || '',
    });
    setSubmitting(true);
    setError(null);
    try {
      let saved;
      if (isEdit) {
        await editar(ot, {
          titulo,
          descripcion,
          fechaCompromiso: dateInputToMs(fechaCompromiso),
          clienteId: clienteId || null,
          contratoId: contratoId || null,
          fianzaId: fianzaId || null,
          afianzadora: afianzadoraDeFianza,
          tipo,
        });
        saved = { ...ot, titulo, descripcion, clienteId, contratoId, fianzaId, tipo };
      } else {
        saved = await crear({
          tipo,
          titulo,
          descripcion,
          clienteId: clienteId || null,
          contratoId: contratoId || null,
          fianzaId: fianzaId || null,
          afianzadora: afianzadoraDeFianza,
          faseId: faseId || null,
          equipos,
          asignados,
          fechaCompromiso: dateInputToMs(fechaCompromiso),
        });
      }
      onSaved?.(saved);
      resetAndClose();
    } catch (e) {
      console.error('guardar OT failed', e);
      setError(e.message || 'No se pudo guardar la orden.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open onClose={submitting ? () => {} : resetAndClose} size="xl">
      <DialogTitle>
        <span className="text-azul-surelia">
          {isEdit ? `Editar ${folioTexto(ot.folio)}` : 'Nueva orden de trabajo'}
        </span>
      </DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div className="sm:w-1/3">
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <select
              value={tipo || ''}
              onChange={(e) => { setTipo(e.target.value); if (!ofreceFianza(e.target.value)) setFianzaId(''); }}
              disabled={isEdit}
              className={`${field} disabled:bg-gray-50 disabled:text-gray-500`}
              autoFocus={!isEdit}
            >
              {config.flujos.map((flujo) => (
                <option key={flujo.id} value={flujo.id}>{flujo.nombre}</option>
              ))}
            </select>
          </div>

          <div className={`grid grid-cols-1 gap-3 ${pideFianza ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fiado (opcional)</label>
              <SearchableListbox
                options={clienteOptions}
                value={clienteId}
                onChange={(v) => { setClienteId(v); if (contratoId && contractsList.find((c) => c.id === contratoId)?.clientId !== v) { setContratoId(''); setFianzaId(''); } }}
                placeholder="Sin fiado todavía"
                popupClassName="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contrato (opcional)</label>
              <SearchableListbox
                options={contratoOptions}
                value={contratoId}
                onChange={elegirContrato}
                placeholder="Sin contrato todavía"
                searchPlaceholder="Número de contrato"
                popupClassName="w-full"
              />
            </div>
            {pideFianza && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {requiereFianza(tipo) ? 'Fianza que se endosa' : 'Fianza (opcional)'}
                </label>
                <SearchableListbox
                  options={fianzaOptions}
                  value={fianzaId}
                  onChange={setFianzaId}
                  placeholder={contratoId ? 'Elige la fianza' : 'Elige el contrato primero'}
                  popupClassName="w-full"
                />
              </div>
            )}
          </div>

          {!isEdit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fase inicial</label>
                <select value={faseId} onChange={(e) => setFaseId(e.target.value)} className={field}>
                  {fases.map((f) => (
                    <option key={f.id} value={f.id}>{f.nombre}{f.diasObjetivo ? ` · ${f.diasObjetivo} d` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Equipos</label>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {config.equipos.map((eq) => {
                    const on = equipos.includes(eq.id);
                    return (
                      <button
                        key={eq.id}
                        type="button"
                        onClick={() => toggleEquipo(eq.id)}
                        className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          on ? 'border-azul-surelia bg-azul-surelia text-white' : 'border-gray-300 text-gray-700 hover:border-azul-surelia/50'
                        }`}
                      >
                        {eq.nombre}
                      </button>
                    );
                  })}
                  {config.equipos.length === 0 && (
                    <span className="text-xs text-gray-400">Sin equipos configurados</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isEdit && (
            <div>
              <div className="flex items-baseline justify-between mb-1">
                <label className="block text-xs font-medium text-gray-600">Asignados</label>
                <span className="text-xs text-zinc-500">
                  {asignados.length === 0 ? 'Nadie todavía' : `${asignados.length} ${asignados.length === 1 ? 'persona' : 'personas'}`}
                </span>
              </div>
              <PanelAsignacion
                miembros={miembros}
                equipos={config.equipos}
                equiposElegidos={equipos}
                carga={carga}
                ejecutivos={ejecutivos}
                asignados={asignados}
                meUid={user?.uid}
                onToggle={toggleAsignado}
              />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fecha compromiso (opcional)</label>
              <input
                type="date"
                value={fechaCompromiso}
                onChange={(e) => setFechaCompromiso(e.target.value)}
                className={field}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Descripción (opcional)</label>
            <textarea
              rows={2}
              value={descripcion}
              maxLength={DESCRIPCION_MAX_LEN}
              onChange={(e) => setDescripcion(e.target.value)}
              className={field}
            />
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-1">
            {!isEdit && !preset?.tipo ? (
              <Button plain onClick={() => setTipo(null)} disabled={submitting}>Cambiar tipo</Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button plain onClick={resetAndClose} disabled={submitting}>Cancelar</Button>
              <Button color="surelia" onClick={guardar} disabled={submitting}>
                {submitting ? 'Guardando…' : isEdit ? 'Guardar' : 'Abrir orden'}
              </Button>
            </div>
          </div>
        </div>
      </DialogBody>
    </Dialog>
  );
}

export default OtDialog;
